/**
 * /review/:tournamentId/:handNumber — step-by-step hand replay.
 *
 * Loads the HandRow, rebuilds frames via buildFrames, drives
 * useReviewPlayer for the transport. Reuses the /play table assembly
 * so visuals stay consistent. Keyboard: ←/→ step, Space toggle,
 * `[`/`]` cycle speed.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { codenameForPlayerId } from '@/characters/definitions';
import { Hud } from '@/components/hud/Hud';
import { HeroPanel } from '@/components/player/HeroPanel';
import { type OpponentDescriptor, OpponentRing } from '@/components/player/OpponentRing';
import { ActionStrip } from '@/components/review/ActionStrip';
import { ReplayControls } from '@/components/review/ReplayControls';
import { resolveDealerSeat } from '@/components/table/DealerPuck';
import { Table } from '@/components/table/Table';
import type { HandState, PlayerId } from '@/engine/types';
import { useReviewPlayer } from '@/hooks/useReviewPlayer';
import { PixiStage } from '@/pixi/Stage';
import { buildFrames, type Frame } from '@/replay/buildFrames';
import { computeGtoDelta } from '@/replay/computeGtoDelta';
import { useSettingsStore } from '@/state/settingsStore';
import type { HandRow } from '@/storage/db';
import { getHand, handId } from '@/storage/handHistory';
import { parseHand } from '@/storage/pokerstars';
import { computePot } from '@/utils/pot';

function opponentsFromState(
  state: HandState,
  heroId: PlayerId,
  showFoldedCards: boolean,
): OpponentDescriptor[] {
  const ordered = [...state.players].filter((p) => p.id !== heroId).sort((a, b) => a.seat - b.seat);
  return ordered.map((p) => ({
    codename: codenameForPlayerId(p.id),
    stack: p.stack,
    isTurn: state.pendingActor === p.id,
    isFolded: p.folded,
    isAllIn: p.allIn,
    isEliminated: p.eliminated,
    inHand: !p.folded && !p.eliminated,
    holeCards: p.holeCards,
    hideHoleCards: !showFoldedCards,
  }));
}

export function HandReplayRoute() {
  const { tournamentId, handNumber } = useParams();
  const handNumberInt = handNumber ? Number(handNumber) : Number.NaN;
  const id =
    tournamentId && !Number.isNaN(handNumberInt) ? handId(tournamentId, handNumberInt) : null;

  const [row, setRow] = useState<HandRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      try {
        const found = await getHand(id);
        if (cancelled) return;
        if (!found) {
          setError(`핸드를 찾을 수 없습니다: ${id}`);
          return;
        }
        setRow(found);
      } catch (e) {
        if (cancelled) return;
        setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const frames: Frame[] = useMemo(() => {
    if (!row) return [];
    try {
      return buildFrames(row);
    } catch (e) {
      setError((e as Error).message);
      return [];
    }
  }, [row]);

  const heroId = useMemo<PlayerId>(() => {
    if (!row) return 'p0';
    try {
      return parseHand(row.pokerstarsHH).heroId;
    } catch {
      return 'p0';
    }
  }, [row]);

  const gtoDelta = useMemo(
    () => (frames.length > 0 && heroId ? computeGtoDelta(frames, heroId) : []),
    [frames, heroId],
  );

  const showFoldedCards = useSettingsStore((s) => s.showFoldedCards);

  const player = useReviewPlayer(frames.length);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLElement &&
        (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')
      ) {
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        player.prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        player.next();
      } else if (e.key === ' ') {
        e.preventDefault();
        player.toggle();
      } else if (e.key === '[') {
        e.preventDefault();
        player.cycleSpeed(-1);
      } else if (e.key === ']') {
        e.preventDefault();
        player.cycleSpeed(1);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [player]);

  if (error) {
    return (
      <main className="min-h-dvh bg-bg text-fg flex flex-col items-center justify-center gap-2 p-4 text-center">
        <p className="text-sm text-danger">{error}</p>
        <Link to={`/review/${tournamentId ?? ''}`} className="text-xs text-accent underline">
          ← 토너먼트 리스트
        </Link>
      </main>
    );
  }

  if (!row || frames.length === 0) {
    return (
      <main className="min-h-dvh bg-bg text-fg flex items-center justify-center">
        <p className="text-sm text-muted">불러오는 중…</p>
      </main>
    );
  }

  const currentFrame = frames[player.step] ?? frames[0];
  if (!currentFrame) {
    return (
      <main className="min-h-dvh bg-bg text-fg flex items-center justify-center">
        <p className="text-sm text-muted">빈 핸드입니다.</p>
      </main>
    );
  }
  const state = currentFrame.state;
  const hero = state.players.find((p) => p.id === heroId);
  const pot = computePot(state);
  const orderedOpps = [...state.players]
    .filter((p) => p.id !== heroId)
    .sort((a, b) => a.seat - b.seat);
  const dealerSeat = resolveDealerSeat(hero?.seat ?? null, state.button, orderedOpps);

  return (
    <main className="min-h-dvh bg-bg text-fg flex flex-col" data-testid="hand-replay-route">
      <header className="flex items-center justify-between px-3 py-2 bg-bg-elev border-b border-border">
        <Link
          to={`/review/${encodeURIComponent(tournamentId ?? '')}`}
          className="text-xs text-accent underline"
        >
          ← 리스트
        </Link>
        <span className="text-xs font-mono">
          {tournamentId} · 핸드 #{handNumberInt}
        </span>
      </header>
      <Hud
        level={state.level.level}
        blinds={{ sb: state.level.sb, bb: state.level.bb, ante: state.level.ante }}
        handNumber={state.handNumber}
        pot={pot}
      />
      <Table pot={pot} board={state.board} bbSize={state.level.bb} dealerSeat={dealerSeat}>
        <PixiStage>
          <div className="absolute inset-x-0 top-0 px-2 pt-2">
            <OpponentRing
              opponents={opponentsFromState(state, heroId, showFoldedCards)}
              bbSize={state.level.bb}
            />
          </div>
        </PixiStage>
      </Table>
      <HeroPanel stack={hero?.stack ?? 0} holeCards={hero?.holeCards} bbSize={state.level.bb} />
      <ActionStrip
        frames={frames}
        heroId={heroId}
        gtoDelta={gtoDelta}
        currentStep={player.step}
        onSeek={player.setStep}
      />
      <ReplayControls
        step={player.step}
        totalSteps={player.totalSteps}
        playing={player.playing}
        speed={player.speed}
        onPrev={player.prev}
        onNext={player.next}
        onToggle={player.toggle}
        onSpeedChange={player.setSpeed}
      />
    </main>
  );
}
